import React, { useEffect } from 'react';

import { Box, Heading, ModalDialog } from '@zillow/constellation';
import { setdim, track } from '@zillow/universal-analytics';
import { hasSeenInterstitial, setSeenInterstitial } from '@zillow/user-onboarding';
import { hideGoogleOneTap } from '@zillow/user-session-store-actions';

import PNG from '../../../public/images/interstitial_graphic.png';
import WEBP from '../../../public/images/interstitial_graphic.webp';
import { cookie } from '../../constants/cookies';
import type { SearchLocation, SearchTerm } from '../../types/searchBox';
import { hasCookie } from '../../utils/cookie';
import ListingButtons from './ListingButtons/ListingButtons';

const GRAPHIC = {
    // eslint-disable-next-line zillow/@typescript-eslint/naming-convention
    PNG,
    // eslint-disable-next-line zillow/@typescript-eslint/naming-convention
    WEBP,
    height: 100,
    width: 262,
};

export function isInterstitialEnabled(inputs: { loggedIn: boolean }) {
    return !inputs.loggedIn && !hasCookie(cookie.search) && !hasSeenInterstitial();
}

export interface InterstialProps {
    searchLocation: SearchLocation;
    searchTerm: SearchTerm;
}

function Interstitial({ searchLocation, searchTerm }: InterstialProps) {
    useEffect(() => {
        hideGoogleOneTap();

        track({
            action: 'Listing_Type_Interstitial_Eligible',
            category: 'ACT',
        });

        setdim({ dimension138: 'home' });
        track({
            action: 'open',
            category: 'dialog',
            label: 'listingTypeInterstitial',
        });
        setSeenInterstitial();
    }, []);

    return (
        <Box
            as={ModalDialog}
            sx={{
                '[class^="DialogBody-"]': {
                    textAlign: 'center',
                    margin: '$spacing.lg',
                },
            }}
            // This modal should not be closeable. Hide this button.
            renderCloseButton={null}
            isOpen
            // eslint-disable-next-line zillow/@typescript-eslint/naming-convention
            size={{ lg_lte: 'fullScreen', xl_gte: 'sm' }}
            renderBody={
                <React.Fragment>
                    <picture>
                        <source
                            srcSet={GRAPHIC.WEBP}
                            type="image/webp"
                            height={GRAPHIC.height}
                            width={GRAPHIC.width}
                        />
                        <Box
                            as="img"
                            // This is just a display image, so hide it from screen
                            // readers by setting the alt text to an empty string.
                            alt=""
                            src={GRAPHIC.PNG}
                            height={GRAPHIC.height}
                            width={GRAPHIC.width}
                            sx={{
                                margin: '$spacing.xs auto $spacing.lg',
                            }}
                        />
                    </picture>

                    <Box
                        as={Heading}
                        level="3"
                        fontType="h5"
                        marginBottom="lg"
                        sx={{
                            'fontWeight': 'normal',
                            '@media $xl': {
                                maxWidth: 275,
                                display: 'inline-block',
                                lineHeight: 'normal',
                            },
                        }}
                    >
                        What type of listings would you like to see?
                    </Box>

                    <ListingButtons location={searchLocation} searchTerm={searchTerm} />
                </React.Fragment>
            }
        />
    );
}

export default Interstitial;
